import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Truck, Phone, Car, ArrowRight } from 'lucide-react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DriverSignup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    vehicle_type: 'tuk_tuk',
    vehicle_number: '',
    phone: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/driver/signup`, formData);
      toast.success('Driver registration successful!');
      // Reload to get new user role
      window.location.href = '/driver';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register as driver');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-stone-50" data-testid="driver-signup">
      <Navbar />
      
      <main className="flex-1 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
              <Truck className="w-10 h-10 text-orange-600" />
            </div>
            <h1 className="text-2xl font-bold text-stone-900">Become a Driver</h1>
            <p className="text-stone-600 mt-2">
              Join our network of tuk-tuk drivers and start earning
            </p>
          </div>

          {/* Benefits */}
          <div className="bg-orange-50 rounded-xl p-6 mb-8">
            <h3 className="font-semibold text-orange-800 mb-3">Why drive with NarkOnline?</h3>
            <ul className="space-y-2 text-sm text-orange-700">
              <li>• Flexible hours - work when you want</li>
              <li>• Fair pay per delivery</li>
              <li>• Support your local community</li>
              <li>• Easy-to-use driver app</li>
            </ul>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-xl p-6 shadow-sm space-y-6">
            <div className="space-y-2">
              <Label>Vehicle Type</Label>
              <Select 
                value={formData.vehicle_type} 
                onValueChange={(v) => setFormData({ ...formData, vehicle_type: v })}
              >
                <SelectTrigger data-testid="vehicle-type-select">
                  <Car className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tuk_tuk">Tuk Tuk</SelectItem>
                  <SelectItem value="motorcycle">Motorcycle</SelectItem>
                  <SelectItem value="bicycle">Bicycle</SelectItem>
                  <SelectItem value="car">Car</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Vehicle Registration Number</Label>
              <Input
                value={formData.vehicle_number}
                onChange={(e) => setFormData({ ...formData, vehicle_number: e.target.value })}
                placeholder="e.g., CA 123-456"
                required
                data-testid="vehicle-number-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Contact Phone Number</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+27 61 530 4587"
                  className="pl-10"
                  required
                  data-testid="phone-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-orange-600 hover:bg-orange-700 py-6 text-lg"
              disabled={loading}
              data-testid="submit-driver-signup"
            >
              {loading ? (
                <span className="spinner w-5 h-5"></span>
              ) : (
                <>
                  Start Driving
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DriverSignup;
